import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Layout from '@/components/Layout';
import { toast } from 'sonner';
import { MailCheck } from 'lucide-react';

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      toast.error('Enter the 6-digit code from your email');
      return;
    }
    setLoading(true);
    try {
      await authAPI.verifyEmail(email, code);
      toast.success('Email verified! Welcome to Bamo Tech Sphere.');
      navigate('/products');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) {
      toast.error('Enter your email first');
      return;
    }
    setResending(true);
    try {
      await authAPI.resendVerification(email);
      toast.success('If that email needs verification, a new code was sent.');
      setCooldown(60);
    } catch (error) {
      toast.error('Could not resend code right now');
    } finally {
      setResending(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center section-padding bg-secondary/30">
        <Card className="w-full max-w-md luxury-card">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-2">
              <MailCheck className="w-10 h-10 text-primary" />
            </div>
            <CardTitle className="text-2xl font-bold">Verify your email</CardTitle>
            <CardDescription>
              Enter the 6-digit code we sent to your email to activate your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleVerify} className="space-y-5">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  data-testid="verify-email-input"
                />
              </div>

              <div>
                <Label htmlFor="code">Verification Code</Label>
                <Input
                  id="code"
                  inputMode="numeric"
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-2xl tracking-[0.5em] font-mono"
                  maxLength={6}
                  required
                  disabled={loading}
                  data-testid="verify-code-input"
                />
              </div>

              <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="verify-email-submit">
                {loading ? 'Verifying...' : 'Verify Email'}
              </Button>
            </form>

            <div className="text-center text-sm mt-6 space-y-2">
              <p className="text-muted-foreground">
                Didn't get a code?{' '}
                <button
                  onClick={handleResend}
                  disabled={resending || cooldown > 0}
                  className="text-primary font-medium hover:underline disabled:opacity-50 disabled:no-underline"
                  data-testid="resend-verification-button"
                >
                  {cooldown > 0 ? `Resend in ${cooldown}s` : resending ? 'Sending...' : 'Resend code'}
                </button>
              </p>
              <p className="text-muted-foreground">
                <Link to="/login" className="text-primary font-medium hover:underline">Back to login</Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default VerifyEmailPage;
